import React from 'react';
import { DANGER, WARNING } from './tokens';
import { useSoft, SOFT, RING_ON } from './SurfaceStyleContext';

const SIZES = {
  cta: { padding: '15px 12px', fontSize: 13, letterSpacing: '0.06em', minHeight: 48 },
  md: { padding: '13px 22px', fontSize: 12, letterSpacing: '0.08em' },
  sm: { padding: '9px 15px', fontSize: 11, letterSpacing: '0.1em' },
  xs: { padding: '5px 10px', fontSize: 10, letterSpacing: '0.1em' },
  pill: { padding: '3px 8px', fontSize: 9, letterSpacing: '0.1em' },
  tap: { padding: '13px 10px', fontSize: 12, letterSpacing: '0.08em', minHeight: 46 },
};

/**
 * The redesign's button. Hover inverts (200ms), focus rings, disabled dims without resizing.
 * variant: fill · outline · danger · warning · onBlack (white on a black panel) · onBlackGhost · ghost (no border)
 */
/** Soft sizes: 54px primary CTA (12px/800), 48px secondary, 38 / 30 / 26 small pills, 46 tap. */
const SOFT_SIZES = {
  cta: { height: 54, padding: '0 20px', fontSize: 12, fontWeight: 800, letterSpacing: '0.14em' },
  md: { height: 48, padding: '0 20px', fontSize: 11, letterSpacing: '0.14em' },
  sm: { height: 38, padding: '0 16px', fontSize: 10, letterSpacing: '0.14em' },
  xs: { height: 30, padding: '0 12px', fontSize: 9, letterSpacing: '0.12em' },
  pill: { height: 26, padding: '0 10px', fontSize: 8, letterSpacing: '0.14em' },
  tap: { height: 46, padding: '0 20px', fontSize: 11, fontWeight: 800, letterSpacing: '0.14em' },
};

/**
 * Soft variants: fill = black pill; outline = the #F2F2F2 secondary pill (hover inverts);
 * white = a white pill (hover inverts); ring = black with the rainbow ring (the Party Starter CTA);
 * danger = red tint; warning = amber tint; ghost = plain underline text.
 */
function softStyle(variant) {
  switch (variant) {
    case 'fill': return { cls: 'text-white hover:bg-[#1F1F1F]', style: { background: '#000' } };
    case 'ring': return { cls: 'text-white', style: { background: RING_ON, border: '2px solid transparent' } };
    case 'white': return { cls: 'text-black hover:bg-black hover:text-white', style: { background: '#fff' } };
    case 'danger': return { cls: 'hover:bg-[#DC2626] hover:text-white', style: { background: SOFT.redTint, color: SOFT.red } };
    case 'warning': return { cls: 'hover:bg-black hover:text-white', style: { background: SOFT.amberTint, color: SOFT.amber } };
    case 'onBlack': return { cls: 'text-black hover:bg-[#E5E5E5]', style: { background: '#fff' } };
    case 'onBlackGhost': return { cls: 'text-white hover:bg-white hover:text-black', style: { background: '#1A1A1A' } };
    case 'ghost': return { cls: 'text-black hover:underline', style: { background: 'transparent' } };
    default: return { cls: 'text-black hover:bg-black hover:text-white', style: { background: SOFT.chip } }; // outline · hairline
  }
}

export default function SurfaceButton({
  variant = 'outline',
  size = 'md',
  fullWidth = false,
  className = '',
  style = {},
  type = 'button',
  children,
  ...rest
}) {
  const base = 'font-mono font-bold uppercase transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 text-center whitespace-nowrap';
  const soft = useSoft();
  if (soft) {
    const s = softStyle(variant);
    return (
      <button
        type={type}
        className={`${base} rounded-full ${s.cls} ${fullWidth ? 'w-full' : ''} ${className}`}
        style={{ border: '2px solid transparent', boxSizing: 'border-box', ...(SOFT_SIZES[size] || SOFT_SIZES.md), ...s.style, ...style }}
        data-soft-variant={variant}
        {...rest}
      >
        {children}
      </button>
    );
  }
  const variants = {
    fill: 'border-2 border-black bg-black text-white hover:bg-white hover:text-black',
    outline: 'border-2 border-black bg-white text-black hover:bg-black hover:text-white',
    danger: 'border-2 bg-white hover:text-white',
    warning: 'border-2 text-black hover:bg-black hover:text-white hover:border-black',
    onBlack: 'border-2 border-white bg-white text-black hover:bg-black hover:text-white',
    onBlackGhost: 'border-2 border-white bg-transparent text-white hover:bg-white hover:text-black',
    ghost: 'border-2 border-transparent bg-transparent text-black hover:underline',
    hairline: 'border-2 bg-white text-black hover:border-black',
  };
  const extra = {};
  if (variant === 'danger') { extra.borderColor = DANGER; extra.color = DANGER; }
  if (variant === 'warning') { extra.borderColor = WARNING; extra.background = WARNING; }
  if (variant === 'hairline') { extra.borderColor = '#E5E7EB'; }
  const v = variants[variant] || variants.outline;
  const hoverDanger = variant === 'danger' ? 'hover:bg-[#DC2626]' : '';
  return (
    <button
      type={type}
      className={`${base} ${v} ${hoverDanger} ${fullWidth ? 'w-full' : ''} ${className}`}
      style={{ ...SIZES[size], ...extra, ...style }}
      {...rest}
    >
      {children}
    </button>
  );
}
